import { Link } from '../../router/Link';
import { useCopy } from '../../strings/useCopy';
import styles from './placeholder.module.css';

/**
 * The honest answer for a route that *parses* but whose screen is a later slice, and for one that
 * does not parse at all.
 *
 * These are different states and are rendered as different pages on purpose. "Channel pages arrive
 * in a later slice" tells a reader the link was good and the app is unfinished; a 404 tells them the
 * link was bad. Collapsing the two is how a working link gets reported as broken — and §8 already
 * insists on the same distinction for `/post/ID`, which must never be a 404.
 *
 * A `<div>` rather than a `<main>`: the shell owns the `<main>` landmark, and a nested
 * one would give the page two.
 */
export interface PlaceholderProps {
  readonly title: string;
  readonly detail: string;
  /** The slice that will build it, when there is one. */
  readonly slice: string | null;
  readonly homeHref: string;
  /**
   * §8 asks for `/post/ID` to carry a jump-out rather than being a dead end — the post is real and the
   * link is good, this build just does not render it. Empty for every other placeholder, where there is
   * nothing outside the app to point at.
   */
  readonly jumpOuts: readonly { readonly url: string; readonly host: string }[];
}

export function PlaceholderView({ title, detail, slice, homeHref, jumpOuts }: PlaceholderProps) {
  const words = useCopy().text.shell.placeholder;
  return (
    <div className={styles.placeholder}>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.detail}>{detail}</p>
      {slice === null ? null : (
        <p className={styles.slice}>
          {words.plannedInSliceBefore} <span className={styles.sliceId}>{slice}</span>.
        </p>
      )}
      {jumpOuts.length === 0 ? null : (
        <p className={styles.detail}>
          {/* Plain anchors: these leave the app. */}
          {words.openOnBefore}{' '}
          {jumpOuts.map((jumpOut) => (
            <a className={styles.jumpOut} key={jumpOut.url} href={jumpOut.url} rel="noreferrer">
              {jumpOut.host}
            </a>
          ))}
          .
        </p>
      )}
      <Link className={styles.back} href={homeHref}>
        {words.backToStart}
      </Link>
    </div>
  );
}
